import { PricingNumericTypeValues } from "@/screens/Pricing/constants"
import { isNil } from "@/utils/utils"

type Nullable = number | null | undefined

export const pricingCalc = {
  currentProfit: ({
    currentPrice,
    acceptedCost,
  }: {
    currentPrice: Nullable
    acceptedCost: Nullable
  }): number | null => {
    if (isNil(acceptedCost) || isNil(currentPrice)) {
      return null
    }

    return currentPrice - acceptedCost
  },

  currentMargin: ({
    currentPrice,
    acceptedCost,
  }: {
    currentPrice: Nullable
    acceptedCost: Nullable
  }): number | null => {
    if (isNil(acceptedCost) || !currentPrice) {
      return null
    }

    return ((currentPrice - acceptedCost) / currentPrice) * 100
  },

  newPrice: ({
    currentPrice,
    currentAcceptedCost,
    changeValue,
    changeType,
    maintainMargin = false,
  }: {
    currentPrice: Nullable
    currentAcceptedCost: Nullable
    changeValue: Nullable
    changeType: PricingNumericTypeValues
    maintainMargin?: boolean
  }): number | null => {
    if (isNil(currentAcceptedCost) || isNil(currentPrice) || isNil(changeValue)) {
      return null
    }

    if (maintainMargin) {
      const currentMargin = pricingCalc.currentMargin({ currentPrice, acceptedCost: currentAcceptedCost })
      const newAcceptedCost = pricingCalc.newAcceptedCost({ currentAcceptedCost, changeValue, changeType })

      if (isNil(currentMargin) || isNil(newAcceptedCost)) {
        return null
      }

      const denominator = 1 - currentMargin / 100
      if (!denominator) {
        return null
      }

      return newAcceptedCost / denominator
    }

    return changeType === PricingNumericTypeValues.Percent
      ? currentPrice * (1 + changeValue / 100)
      : currentPrice + changeValue
  },

  newAcceptedCost: ({
    currentAcceptedCost,
    changeValue,
    changeType,
  }: {
    currentAcceptedCost: Nullable
    changeValue: Nullable
    changeType: PricingNumericTypeValues
  }): number | null => {
    if (isNil(currentAcceptedCost) || isNil(changeValue)) {
      return null
    }

    return changeType === PricingNumericTypeValues.Percent
      ? currentAcceptedCost * (1 + changeValue / 100)
      : currentAcceptedCost + changeValue
  },

  newProfit: ({ newPrice, acceptedCost }: { newPrice: Nullable; acceptedCost: Nullable }): number | null => {
    if (isNil(acceptedCost) || isNil(newPrice)) {
      return null
    }

    return newPrice - acceptedCost
  },

  newMargin: ({
    newPrice,
    acceptedCost,
  }: {
    newPrice: number | null | undefined
    acceptedCost: number | null | undefined
  }): number | null => {
    if (!newPrice || !acceptedCost) {
      return null
    }

    return ((newPrice - acceptedCost) / newPrice) * 100
  },

  surcharge: ({
    surcharge1Price,
    surcharge2Price,
    surcharge3Price,
  }: {
    surcharge1Price: Nullable
    surcharge2Price: Nullable
    surcharge3Price: Nullable
  }): number => (surcharge1Price ?? 0) + (surcharge2Price ?? 0) + (surcharge3Price ?? 0),
}
